import type { GalleryMediaCategory, GalleryMediaItem } from "@/components/common/gallery";
import type { MediaItem, SUPType } from "@/types/sup";
import { unitSpaces } from "@/utils/sup/sort-media-list";

export interface GalleryProps {
	coverImage?: string;
	mediaList?: MediaItem[];
	projectId?: string;
	projectName?: string;
	className?: string;
	type?: SUPType;
	isLoading?: boolean;
	shouldPrioritizeCoverImage?: boolean;
	sizes?: string;
	onMainImageClick?: () => void;
	categories?: GalleryMediaCategory[];
	/**
	 * Controls the aspect ratio/height behavior of the gallery
	 * - 'square': Square aspect ratio on all screen sizes
	 * - 'fixed-height': Square on mobile, min-h-500px on desktop (container @4xl breakpoint = 896px)
	 * - 'default': No constraints, fully inherits from wrapper - use for cards with aspect ratio wrappers
	 * @default 'default'
	 */
	aspectRatio?: "square" | "fixed-height" | "default";
	/**
	 * Vertical alignment of images
	 * - 'center': Centered vertically (default)
	 * - 'top': Top-aligned on mobile, centered on desktop - use for cards with bottom overlays
	 * @default 'center'
	 */
	imageAlignment?: "center" | "top";
	/**
	 * Use darker background for better contrast (e.g., for card galleries with text overlays)
	 * @default false
	 */
	darkerBackground?: boolean;
	/**
	 * Autoplay video on load (for preference list)
	 * @default false
	 */
	videoAutoplayOnLoad?: boolean;
}

export const defaultCategories: GalleryMediaCategory[] = [
	{ name: "Videos", code: "videos" },
	{ name: "Hall", code: "halls" },
	{ name: "Bedroom", code: "bedrooms" },
	{ name: "Balcony", code: "balconies" },
	{ name: "Kitchen", code: "kitchens" },
	{ name: "Amenities", code: "amenities" },
	{ name: "Bathroom", code: "bathrooms" },
	{ name: "Others", code: "others" },
	{ name: "Parking", code: "parkings" },
	{ name: "Pooja Room", code: "poojaRooms" },
	{ name: "Utility", code: "utilities" },
];

/**
 * Filter categories to only those with media, then sort by unitSpaces priority.
 */
export function getSortedCategories(
	categories: GalleryMediaCategory[] | undefined,
	media: GalleryMediaItem[],
): GalleryMediaCategory[] {
	return (categories || defaultCategories)
		.filter(cat => media.some(item => item.space === cat.code))
		.sort((a, b) => {
			const getCategoryPriority = (category: GalleryMediaCategory): number => {
				// Videos always come first
				if (category.code === "videos") return -1;
				const index = unitSpaces.indexOf(category.code);
				if (index !== -1) return index;
				return 1000;
			};

			const priorityA = getCategoryPriority(a);
			const priorityB = getCategoryPriority(b);

			if (priorityA !== priorityB) {
				return priorityA - priorityB;
			}

			return a.name.localeCompare(b.name);
		});
}

/**
 * Filter out items with the 'hidden' tag from mediaList.
 */
export function filterHiddenMedia(mediaList?: MediaItem[]): MediaItem[] {
	if (!mediaList) return [];
	return mediaList.filter(item => !item.tags?.includes("hidden"));
}
